import axios from "axios";

/*
 * What sort of part a spare part is: /api/spare-part-types.
 *
 * A controlled vocabulary and nothing more — bearing, gasket, filter. A type has no balance, no
 * hierarchy and no location; it is a word the register picks from instead of one somebody types,
 * which is what stops "Tätning", "tätning" and "Tätning " from being three kinds of seal to every
 * list that reads them.
 *
 * Shaped like cmmsStorageLocation.jsx, down to throwResponseData — the CMMS controllers answer a
 * failure with ApiErrorResponse { code, message }, and rethrowing the body rather than the axios
 * error is what lets formatCmmsError print "A spare part type named 'Bearing' already exists for
 * this company" instead of "Request failed with status code 400". A duplicate name has its own
 * code here, CMMS_SPARE_PART_TYPE_NAME_TAKEN, where a location's clash only says CMMS_VALIDATION.
 *
 * Nothing here sends a user id. The server takes "who did this" from the token and overwrites
 * anything a request supplied, so passing one would only suggest it mattered.
 */
const baseUrl = "/api/spare-part-types";

const throwResponseData = (error) => {
  throw error.response?.data ?? error;
};

export const getSparePartTypes = async (companyKey, includeInactive = false) => {
  try {
    const response = await axios.get(baseUrl, { params: { companyKey, includeInactive } });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * No caller yet. Kept because it is the sixth member of the service contract, and a fetcher
 * missing one of six reads as though it maps a different API than it does.
 */
export const getSparePartTypeById = async (companyKey, id) => {
  try {
    const response = await axios.get(`${baseUrl}/${id}`, { params: { companyKey } });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const createSparePartType = async (sparePartType) => {
  try {
    const response = await axios.post(baseUrl, sparePartType);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * A rename, in place. The body carries no isActive: retiring a type is a decision of its own with
 * its own endpoint, not something an edit to the name should be able to do in passing.
 *
 * The parts of that type follow the rename, which is the whole reason types are a table rather
 * than free text on the part.
 */
export const updateSparePartType = async (id, sparePartType) => {
  try {
    const response = await axios.put(`${baseUrl}/${id}`, sparePartType);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * Retire, not delete. There is no DELETE endpoint on purpose: SparePart.SparePartTypeId names
 * these rows, and removing one would leave every part of that kind with no way to say what it is.
 *
 * Deactivating is never refused, however many parts carry the type. They keep naming it and keep
 * resolving its name — the server does not check IsActive when a part is saved, so editing a part
 * whose type was retired last week still works. What stops is the type being offered for anything
 * new.
 *
 * Answers 204, so response.data is empty — returned anyway to keep the six shaped alike.
 */
export const deactivateSparePartType = async (companyKey, id) => {
  try {
    const response = await axios.post(`${baseUrl}/${id}/deactivate`, null, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const activateSparePartType = async (companyKey, id) => {
  try {
    const response = await axios.post(`${baseUrl}/${id}/activate`, null, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};
